import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Globe, Trash2, RotateCw } from "lucide-react";
import { api, extractApiError } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { formatDateTime } from "../lib/dateTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageLoadError } from "./NoAccess";
import { useScreenWrites } from "./screenWrites";

const STATUS_CLS = {
  pending_dns: "bg-amber-50 text-amber-700",
  provisioning: "bg-sky-50 text-sky-700",
  live: "bg-emerald-50 text-emerald-700",
  failed: "bg-rose-50 text-rose-700",
  lapsed: "bg-amber-50 text-amber-700",
};

/**
 * `/super-admin/custom-domains` — every practice's custom domain (backend
 * modules/custom_domains, GET /super-admin/custom-domains, tenants.read): its status,
 * last DNS check and failure, with the operator's two doors — Re-check (the
 * practitioner's Verify on their behalf) and Remove (tenants.write) — and the two
 * release windows (settings.write): how long a lapsed domain is held, and how long a
 * domain that never went live stays reserved.
 */

const POLICY_FIELDS = [
  { key: "lapse_grace_days", label: "cdm.policy.lapse", testId: "custom-domains-grace-input" },
  { key: "unproven_release_days", label: "cdm.policy.unproven", testId: "custom-domains-unproven-input" },
];
export default function CustomDomainsManager() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const can = useScreenWrites("customDomains");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [policyForm, setPolicyForm] = useState({});
  const [savingPolicy, setSavingPolicy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/super-admin/custom-domains");
      setData(r.data);
      setPolicyForm(Object.fromEntries(POLICY_FIELDS.map((f) => [f.key, String(r.data.policy[f.key])])));
    } catch (e) { setLoadError(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmt = (iso) => formatDateTime(iso, lang);
  const statusLabel = (s) => t(`cdm.status.${s}`);

  const recheck = async (row) => {
    setBusy(`recheck-${row.tenant_id}`);
    try {
      const r = await api.post(`/super-admin/custom-domains/${row.tenant_id}/recheck`);
      if (r.data.edge_error) toast.error(t("cdm.edgeError", { message: r.data.edge_error }));
      else toast.success(t("cdm.rechecked", { domain: row.domain, status: statusLabel(r.data.status) }));
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("cdm.error")));
    } finally { setBusy(null); }
  };

  const remove = async (row) => {
    if (!window.confirm(t("cdm.removeConfirm", { domain: row.domain, practice: row.name || row.slug }))) return;
    setBusy(`remove-${row.tenant_id}`);
    try {
      await api.post(`/super-admin/custom-domains/${row.tenant_id}/remove`);
      toast.success(t("cdm.removed", { domain: row.domain }));
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("cdm.error")));
    } finally { setBusy(null); }
  };

  const savePolicy = async () => {
    const values = {};
    for (const f of POLICY_FIELDS) {
      const [min, max] = data.policy.bounds[f.key];
      const n = Number(policyForm[f.key]);
      if (!Number.isInteger(n) || n < min || n > max) {
        toast.error(t("cdm.policy.invalid", { min, max }));
        return;
      }
      values[f.key] = n;
    }
    setSavingPolicy(true);
    try {
      const r = await api.put("/super-admin/custom-domains/policy", values);
      setData((d) => ({ ...d, policy: r.data }));
      setPolicyForm(Object.fromEntries(POLICY_FIELDS.map((f) => [f.key, String(r.data[f.key])])));
      toast.success(t("cdm.policy.saved"));
    } catch (e) {
      toast.error(extractApiError(e, t("cdm.error")));
    } finally { setSavingPolicy(false); }
  };

  return (
    <div data-testid="custom-domains-manager">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("cdm.title")}</h1>
          <p className="text-slate-500 max-w-2xl">{t("cdm.subtitle")}</p>
        </div>
        <Button onClick={load} variant="outline" className="border-slate-200 text-slate-800/70" data-testid="reload-custom-domains">
          <RefreshCcw className="w-4 h-4 mr-2" /> {t("cdm.reload")}
        </Button>
      </div>

      <PageLoadError error={loading ? null : loadError} onRetry={load} testId="custom-domains-load-error" />
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-amber-600" /> : loadError || !data ? null : (
        <>
          <div className="rounded-2xl bg-white border border-slate-200 p-5 mb-6 max-w-2xl" data-testid="custom-domains-policy">
            <h3 className="font-sans font-semibold mb-1">{t("cdm.policy.title")}</h3>
            <p className="text-sm text-slate-500 mb-3">{t("cdm.policy.body")}</p>
            <div className="flex items-end gap-3 flex-wrap">
              {POLICY_FIELDS.map((f) => (
                <label key={f.key} className="text-xs text-slate-500">
                  {t(f.label)}
                  <Input
                    type="number"
                    min={data.policy.bounds[f.key][0]}
                    max={data.policy.bounds[f.key][1]}
                    value={policyForm[f.key] ?? ""}
                    onChange={(e) => setPolicyForm((p) => ({ ...p, [f.key]: e.target.value }))}
                    disabled={!can.policy}
                    className="mt-1 w-28"
                    data-testid={f.testId}
                  />
                </label>
              ))}
              {can.policy && (
                <Button onClick={savePolicy} disabled={savingPolicy} data-testid="custom-domains-grace-save">
                  {savingPolicy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {t("cdm.policy.save")}
                </Button>
              )}
            </div>
            {data.policy.updated_at && (
              <p className="text-xs text-slate-400 mt-2">{t("cdm.policy.updated", { date: fmt(data.policy.updated_at), who: data.policy.updated_by || "—" })}</p>
            )}
          </div>

          {data.teardowns_pending > 0 && (
            <p className="text-xs text-slate-500 mb-3" data-testid="custom-domains-teardowns">{t("cdm.teardowns", { n: data.teardowns_pending })}</p>
          )}

          <div className="rounded-2xl bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-3">{t("cdm.h.domain")}</th>
                  <th className="text-left px-4 py-3">{t("cdm.h.practice")}</th>
                  <th className="text-left px-4 py-3">{t("cdm.h.status")}</th>
                  <th className="text-left px-4 py-3">{t("cdm.h.checked")}</th>
                  <th className="text-left px-4 py-3">{t("cdm.h.detail")}</th>
                  {(can.manage) && <th className="text-right px-4 py-3">{t("cdm.h.actions")}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((row) => (
                  <tr key={row.tenant_id} data-testid={`custom-domain-row-${row.tenant_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-slate-400" /><code>{row.domain}</code></div>
                      <div className="text-xs text-slate-400 mt-0.5">{t(`cdm.kind.${row.kind}`)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/super-admin/tenants/${row.tenant_id}`} className="text-amber-700 hover:underline">{row.name || row.slug || row.tenant_id}</Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs rounded-full px-2 py-0.5 ${STATUS_CLS[row.status] || "bg-slate-100"}`} data-testid={`custom-domain-status-${row.tenant_id}`}>{statusLabel(row.status)}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{fmt(row.checked_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {row.failure ? <code title={row.failure.message}>{row.failure.code}</code> : row.codes.length ? <code>{row.codes.join(", ")}</code> : "—"}
                    </td>
                    {can.manage && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => recheck(row)} disabled={busy !== null} data-testid={`custom-domain-recheck-${row.tenant_id}`}>
                          {busy === `recheck-${row.tenant_id}` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCw className="w-3 h-3 mr-1" />} {t("cdm.recheck")}
                        </Button>{" "}
                        <Button size="sm" variant="outline" onClick={() => remove(row)} disabled={busy !== null} className="text-rose-600 border-rose-200" data-testid={`custom-domain-remove-${row.tenant_id}`}>
                          {busy === `remove-${row.tenant_id}` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />} {t("cdm.remove")}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr><td colSpan={can.manage ? 6 : 5} className="px-6 py-10 text-slate-400 text-center">{t("cdm.empty")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
